import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { DayLabor, formatTime } from '../my-hours.utils';
import { WorkorderLaborWithContext } from '@certhon/models';

interface WeekGridProps {
  days: DayLabor[];
  defaultExpanded?: boolean;
  isLoading?: boolean;
}

// Default work hours range
const DEFAULT_START_HOUR = 7;
const DEFAULT_END_HOUR = 17; // 5 PM
const PADDING_HOURS = 1;

export function WeekGrid({
  days,
  defaultExpanded = false,
  isLoading = false,
}: WeekGridProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Calculate the hour range based on actual labor entries
  const { startHour, endHour } = useMemo(() => {
    let minHour = DEFAULT_START_HOUR;
    let maxHour = DEFAULT_END_HOUR;

    days.forEach((day) => {
      day.entries.forEach((entry) => {
        const startH = entry.date_start.getHours();
        const endH =
          entry.date_stop.getHours() +
          (entry.date_stop.getMinutes() > 0 ? 1 : 0);
        minHour = Math.min(minHour, startH);
        maxHour = Math.max(maxHour, endH);
      });
    });

    // Add padding but stay within 0-24
    return {
      startHour: Math.max(0, minHour - PADDING_HOURS),
      endHour: Math.min(24, maxHour + PADDING_HOURS),
    };
  }, [days]);

  const hours = useMemo(() => {
    const result = [];
    for (let h = startHour; h < endHour; h++) {
      result.push(h);
    }
    return result;
  }, [startHour, endHour]);

  const totalHours = endHour - startHour;

  return (
    <div className="bg-card rounded-lg border">
      {/* Header - collapsible toggle */}
      <Button
        variant="ghost"
        className="flex w-full items-center justify-between px-4 py-3"
        onClick={() => setIsExpanded(!isExpanded)}
        tooltip={false}
      >
        <span className="text-sm font-semibold">Week Overview</span>
        {isExpanded ? (
          <Minus className="h-4 w-4" />
        ) : (
          <Plus className="h-4 w-4" />
        )}
      </Button>

      {/* Grid content */}
      {isExpanded && (
        <div className="border-t px-4 pt-2 pb-4">
          <div className="flex">
            {/* Hour labels column */}
            <div className="w-12 shrink-0 pr-2">
              {hours.map((hour) => (
                <div
                  key={hour}
                  className="text-muted-foreground text-right text-xs"
                  style={{ height: `${100 / totalHours}%`, minHeight: '2rem' }}
                >
                  {hour.toString().padStart(2, '0')}:00
                </div>
              ))}
            </div>

            {/* Day columns */}
            <div className="grid flex-1 grid-cols-7 gap-1">
              {days.map((day) => (
                <DayColumn
                  key={day.date.toISOString()}
                  day={day}
                  startHour={startHour}
                  endHour={endHour}
                  totalHours={totalHours}
                  isLoading={isLoading}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface DayColumnProps {
  day: DayLabor;
  startHour: number;
  endHour: number;
  totalHours: number;
  isLoading?: boolean;
}

function DayColumn({
  day,
  startHour,
  endHour,
  totalHours,
  isLoading,
}: DayColumnProps) {
  const dayAbbrev = day.dayName.slice(0, 2);

  return (
    <div className="flex flex-col">
      {/* Day header */}
      <div className="mb-1 text-center text-xs font-semibold">{dayAbbrev}</div>

      {/* Time grid */}
      <div
        className="bg-muted/30 relative rounded border"
        style={{ minHeight: `${totalHours * 2}rem` }}
      >
        {/* Hour grid lines */}
        {Array.from({ length: totalHours }).map((_, i) => (
          <div
            key={i}
            className="border-muted absolute right-0 left-0 border-t"
            style={{ top: `${(i / totalHours) * 100}%` }}
          />
        ))}

        {/* Loading skeleton */}
        {isLoading && (
          <div className="absolute inset-1 flex flex-col gap-1">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        )}

        {/* Labor blocks */}
        {!isLoading &&
          day.entries.map((entry) => (
            <LaborBlock
              key={entry.recnum}
              entry={entry}
              startHour={startHour}
              totalHours={totalHours}
            />
          ))}
      </div>
    </div>
  );
}

interface LaborBlockProps {
  entry: WorkorderLaborWithContext;
  startHour: number;
  totalHours: number;
}

function LaborBlock({ entry, startHour, totalHours }: LaborBlockProps) {
  const startMinutes =
    (entry.date_start.getHours() - startHour) * 60 +
    entry.date_start.getMinutes();
  const endMinutes =
    (entry.date_stop.getHours() - startHour) * 60 +
    entry.date_stop.getMinutes();

  const topPercent = (startMinutes / (totalHours * 60)) * 100;
  const heightPercent = ((endMinutes - startMinutes) / (totalHours * 60)) * 100;

  const isCompleted = entry.workorder.completed;

  const label = `W${entry.workorder_id} ${entry.workorder.customer_name || 'Unknown'}`;

  return (
    <Link
      to={`/workorders/${entry.workorder_id}/labor`}
      className={cn(
        `absolute right-0.5 left-0.5 overflow-hidden rounded px-1 text-xs
        transition-opacity hover:opacity-80`,
        isCompleted
          ? 'bg-green-500 text-green-50'
          : 'bg-amber-500 text-amber-50',
      )}
      style={{
        top: `${topPercent}%`,
        height: `${Math.max(heightPercent, 3)}%`, // Minimum 3% height for visibility
      }}
      title={`${label}\n${formatTime(entry.date_start)} - ${formatTime(entry.date_stop)}`}
    >
      <span className="truncate">{label}</span>
    </Link>
  );
}
